import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { fileService } from '../services/api';
import { FileResponse } from '../types/moldOrder';
import { EntityType } from '../types/mold';

interface FileContextType {
  files: FileResponse[];
  loading: boolean;
  error: string | null;
  fetchFiles: (entityType: EntityType, entityId: number) => Promise<void>;
  hasFileWithTag: (tag: string) => boolean;
  deleteFilesByTag: (tagName: string) => Promise<{ isSuccess: boolean; errorMessage?: string }>;
  addFile: (file: FileResponse) => void;
  removeFile: (fileName: string) => void;
  replaceFiles: (files: FileResponse[]) => void;
}

const FileContext = createContext<FileContextType | undefined>(undefined);

export const FileProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [files, setFiles] = useState<FileResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFiles = useCallback(async (entityType: EntityType, entityId: number) => {
    if (!entityId) return;
    
    setLoading(true);
    try {
      const response = await fileService.getFiles(entityType, entityId);
      setFiles(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch files');
      console.error('Error fetching files:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const hasFileWithTag = useCallback((tag: string) => {
    return files.some((file) => file.tag === tag);
  }, [files]);

  const deleteFilesByTag = useCallback(async (tagName: string) => {
    return await fileService.deleteImagesByTag(tagName);
  }, []);

  const addFile = useCallback((file: FileResponse) => {
    setFiles(prevFiles => [...prevFiles, file]);
  }, []);

  const removeFile = useCallback((fileName: string) => {
    setFiles(prevFiles => prevFiles.filter(file => file.fileName !== fileName));
  }, []);

  const replaceFiles = useCallback((nextFiles: FileResponse[]) => {
    setFiles(nextFiles);
  }, []);

  return (
    <FileContext.Provider value={{
      files,
      loading,
      error,
      fetchFiles,
      hasFileWithTag,
      deleteFilesByTag,
      addFile,
      removeFile,
      replaceFiles
    }}>
      {children}
    </FileContext.Provider>
  );
};

export const useFileContext = () => {
  const context = useContext(FileContext);
  if (context === undefined) {
    throw new Error('useFileContext must be used within a FileProvider');
  }
  return context;
};
